import type {
	Dictionary,
	DictionaryKey,
	PluralDictionaryKey,
} from 'dictionary';
import { formatTemplate, type FormatTemplateValue } from 'utils/format';

type TParams = Record<string, FormatTemplateValue>;

export class I18n {
	private dict: Dictionary;

	private pluralRules: Intl.PluralRules;

	constructor(dictionary: Dictionary, locale?: string) {
		this.dict = dictionary;
		this.pluralRules = new Intl.PluralRules(locale);
	}

	setDictionary = (dictionary: Dictionary) => {
		this.dict = dictionary;
	};

	private getText = ({
		fallback,
		key,
		params,
	}: {
		fallback: string;
		key: string;
		params?: TParams;
	}): string => {
		if (!key) {
			return '';
		}
		const text = this.dict[key] || fallback;
		return params ? formatTemplate(text, params) : text;
	};

	private getPluralText = ({
		fallback,
		key,
		num,
		params,
	}: {
		fallback: string;
		key: string;
		num: number;
		params?: TParams;
	}): string => {
		if (!key) {
			return '';
		}
		const pluralKey = `${key}_${this.pluralRules.select(num)}`;
		const text = this.dict[pluralKey] || this.dict[key] || fallback;
		return formatTemplate(text, { number: num, ...params });
	};

	/**
	 * Retrieve a translation, returning the key if it's missing.
	 *
	 * @example
	 *
	 * t('hello_text')
	 * // => 'Hi there'
	 *
	 * t('missing_key')
	 * // => 'missing_key'
	 *
	 * t('welcome_message', { name: 'Joe' })
	 * // With key text `Welcome {{name}}!`
	 * // => 'Welcome Joe!'
	 */
	t = (key: DictionaryKey, params?: TParams): string =>
		this.getText({
			key,
			fallback: key,
			params,
		});

	/**
	 * Retrieve a translation, returning an empty string if it's missing.
	 *
	 * @example
	 *
	 * tEmpty('hello_text')
	 * // => 'Hi there'
	 *
	 * tEmpty('missing_key')
	 * // => ''
	 */
	tEmpty = (key: DictionaryKey, params?: TParams): string =>
		this.getText({ key, fallback: '', params });

	/**
	 * Retrieve a plural translation, falling back to the base translation and
	 * finally returning the key if it's missing.
	 *
	 * The number will be supplied as the template variable `number`.
	 *
	 * Possible key suffixes, depending on language: `zero`, `one`, `two`, `few`,
	 * `many` and `other`.
	 *
	 * @example
	 *
	 * // With the following keys:
	 * {
	 *   products: `{{number}} products`,
	 *   products_one: `{{number}} product`,
	 * }
	 * tPlural('products', 18)
	 * // => '18 products'
	 * tPlural('products', 1)
	 * // => '1 product'
	 *
	 * // With additional template values:
	 * {
	 *   products: `{{name}} has {{number}} products in the cart`,
	 *   products_one: `{{name}} has one product in the cart`,
	 * }
	 * tPlural('products', 3, { name: 'Joe' })
	 * // => 'Joe has 3 products in the cart'
	 * tPlural('products', 1, { name: 'Joe' })
	 * // => 'Joe has one product in the cart'
	 */
	tPlural = (
		key: PluralDictionaryKey,
		num: number,
		params: TParams = {},
	): string => this.getPluralText({ key, fallback: key, num, params });

	/**
	 * Retrieve a plural translation, falling back to the base translation and
	 * finally returning an empty string if it's missing.
	 *
	 * @see tPlural()
	 */
	tPluralEmpty = (
		key: PluralDictionaryKey,
		num: number,
		params: TParams = {},
	): string => this.getPluralText({ key, fallback: '', num, params });
}
